import { createContext, useContext, useState, useCallback, useMemo } from 'react';
import type { ReactNode, FunctionComponent, ComponentType } from 'react';
import type { ButtonType } from '../components/generic';
import { createHOCDisplayName, dropAt } from '../utils';

export type DialogType = 'alert' | 'confirm';

export type DialogProps = {
  title: string;
  body: string | ReactNode;
  onClose: () => void;
};

export type AlertDialogProps = DialogProps & {
  type: 'alert';
};

export type ConfirmDialogProps = DialogProps & {
  type: 'confirm';
  onConfirm: () => void;
  confirmText?: string;
  confirmBtnType?: ButtonType;
  cancelText?: string;
  allowEnterConfirm?: boolean;
};

export type DialogItem =
  | AlertDialogProps
  | ConfirmDialogProps;

//  type is only used by this context, and onClose is passed directly/doesn't need to be added by the user.
export type DialogItemProps<DI> = Omit<DI, 'type' | 'onClose'>;

//  Returned from each method is another method which will remove the dialog.
type RemoveDialogCallback = () => void;

//  The methods used throughout the app to "call" into the context.
export type DialogState = {
  state: DialogItem[];
  onConfirm: (state: DialogItemProps<ConfirmDialogProps>) => RemoveDialogCallback;
  onAlert: (state: DialogItemProps<AlertDialogProps>) => RemoveDialogCallback;
  //  [nit] index is brittle/can't be used as a React key. Consider using a uuid.
  onRemoveDialog: (dialogIndex: number) => void;
  //  Returns true if there is any dialog open.
  isAnyDialogOpen: boolean;
};

//  Create the context.
const DialogContext = createContext<DialogState>({} as DialogState);

//  Export hooks for components to consume.
export const useDialogContext = () => useContext<DialogState>(DialogContext);
//  Convenience hooks for the most common methods.
export const useOnConfirm = () => useDialogContext().onConfirm;
export const useOnAlert = () => useDialogContext().onAlert;

/**
 * Manages the state of any of the dialog elements on the page.
 *
 * @param {ReactNode} children - Children which context will be provided.
 */
const DialogContextProvider: FunctionComponent = ({ children }) => {
  const [state, setState] = useState<DialogItem[]>([]);

  //  Remove a dialog item at a given index.
  const onRemoveDialog = useCallback((dialogIndex: number) => {
    setState((prevState: DialogItem[]) => dropAt(prevState, dialogIndex));
  }, []);

  //  Add a new dialog item to the stack.
  const onAddDialog = useCallback((item: DialogItem) => {
    //  Create an onClose handler.
    const removalIndex = state.length;
    const onClose = () => onRemoveDialog(removalIndex);
    //  If confirmation dialog, we want to call onClose after onConfirm is called.
    const onConfirm = item.type === 'confirm'
      ? () => {
          item.onConfirm();
          onClose();
        }
      : undefined;
    setState((prevState: DialogItem[]) => prevState.concat({ ...item, onClose, onConfirm } as DialogItem));
    //  Return a remove fn bound with the index, in case consumers want to manually close the dialog.
    return onClose;
  }, [state, onRemoveDialog]);

  //  Store a memoized context value for the dialog state.
  const contextValue: DialogState = useMemo(() => ({
    state,
    onConfirm: (dialogItem: DialogItemProps<ConfirmDialogProps>) => onAddDialog({ ...dialogItem, type: 'confirm' } as ConfirmDialogProps),
    onAlert: (dialogItem: DialogItemProps<AlertDialogProps>) => onAddDialog({ ...dialogItem, type: 'alert' } as AlertDialogProps),
    onRemoveDialog,
    isAnyDialogOpen: state.length > 0,
  }), [
    state,
    //  Added this to supress warning; these won't change across renders because of useCallback.
    onAddDialog,
    onRemoveDialog,
  ]);

  return (
    <DialogContext.Provider value={contextValue}>
      {children}
    </DialogContext.Provider>
  );
};

/**
 * Higher-order component which will "inject" the dialog context into a provided component.
 * Allows us to `useDialogContext` in class components (because hooks don't work in class components).
 * Note that this is intended to just be used as a stop-gap until we can convert all components to functional,
 * where we can simply useDialogContext directly.
 */
export function WithDialogContext<P extends Record<string, unknown>>(Component: ComponentType<P>) {
  const WrappedComponent: FunctionComponent<P> = (props: P) => {
    const dialogContext = useDialogContext();
    return (
      <Component
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
        dialogContext={dialogContext}
      />
    );
  };
  WrappedComponent.displayName = createHOCDisplayName('withDialogContext', Component);
  return WrappedComponent;
}

export default DialogContextProvider;
